import { Translate } from '@chipinside/translate'

import { ContractEventType } from '#/@types/backend'
import { FormProps, UseFormsProps } from '#/store/hooks/useForms/types'
import { endpoint } from '#/store/services/endpoints'

export type WithFarm = { farm: string }

const contractForms = {
  cancellation: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.cancellation.form(params),
      save: {
        controller: endpoint.farm.contract.cancellation.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'cancellation' }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.cancellation.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.cancellation.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'cancellation' }),
        },
      }),
      width: 'xs',
    }),
  },

  cancellation_request: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.cancellation_request.form(params),
      save: {
        controller: endpoint.farm.contract.cancellation_request.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'cancellation_request' }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.cancellation_request.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.cancellation_request.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'cancellation_request' }),
        },
      }),
      width: 'xs',
    }),
  },

  collar_down_sell: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.down_sell.form(params),
      save: {
        controller: endpoint.farm.contract.down_sell.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'collar_down_sell' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.down_sell.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.down_sell.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'collar_down_sell' }),
        },
      }),
      width: 'md',
    }),
  },

  collar_up_sell: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.up_sell.form(params),
      save: {
        controller: endpoint.farm.contract.up_sell.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'collar_up_sell' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.up_sell.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.up_sell.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'collar_up_sell' }),
        },
      }),
      width: 'md',
    }),
  },

  cowconnect_payment_start: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.payment_start.form(params),
      save: {
        controller: endpoint.farm.contract.payment_start.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'cowconnect_payment_start' }),
      width: 'md',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.payment_start.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.payment_start.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'cowconnect_payment_start' }),
        },
      }),
      width: 'md',
    }),
  },

  cross_sell_cowconnect_plan: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.cross_sell_cowconnect.form(params),
      save: {
        controller: endpoint.farm.contract.cross_sell_cowconnect.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'cross_sell_cowconnect_plan' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.cross_sell_cowconnect.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.cross_sell_cowconnect.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'cross_sell_cowconnect_plan',
          }),
        },
      }),
      width: 'md',
    }),
  },

  cross_sell_monitoring_plan: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.cross_sell_monitoring.form(params),
      save: {
        controller: endpoint.farm.contract.cross_sell_monitoring.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'cross_sell_monitoring_plan' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.cross_sell_monitoring.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.cross_sell_monitoring.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'cross_sell_monitoring_plan',
          }),
        },
      }),
      width: 'md',
    }),
  },

  farm_contract: {
    delete: ({
      params,
    }: FormProps<
      WithFarm & {
        contractId: string
        contractName: string
        contractType: ContractEventType
      }
    >): UseFormsProps => {
      let controller = ''

      const parameters = {
        contract: params?.contractId,
        farm: params?.farm,
      }

      switch (params?.contractType) {
        case 'monitoring_activation': {
          controller = endpoint.farm.contract.activation.self(parameters)
          break
        }
        case 'cancellation': {
          controller = endpoint.farm.contract.cancellation.self(parameters)
          break
        }
        case 'temporary_suspension': {
          controller = endpoint.farm.contract.suspension.self(parameters)
          break
        }
        case 'reinstate_contract': {
          controller = endpoint.farm.contract.reinstate.self(parameters)
          break
        }
        case 'collar_up_sell': {
          controller = endpoint.farm.contract.up_sell.self(parameters)
          break
        }
        case 'collar_down_sell': {
          controller = endpoint.farm.contract.down_sell.self(parameters)
          break
        }
        case 'monitoring_contract': {
          controller = endpoint.farm.contract.monitoring.self(parameters)
          break
        }
        case 'reactivation_cowconnect': {
          controller = endpoint.farm.contract.reactivation_cowconnect.self(parameters)
          break
        }
        case 'reactivation_monitoring': {
          controller = endpoint.farm.contract.reactivation_monitoring.self(parameters)
          break
        }
        case 'cowconnect_payment_start': {
          controller = endpoint.farm.contract.payment_start.self(parameters)
          break
        }
        case 'cross_sell_monitoring_plan': {
          controller = endpoint.farm.contract.cross_sell_monitoring.self(parameters)
          break
        }
        case 'cross_sell_cowconnect_plan': {
          controller = endpoint.farm.contract.cross_sell_cowconnect.self(parameters)
          break
        }
        case 'cancellation_request': {
          controller = endpoint.farm.contract.cancellation_request.self(parameters)
          break
        }
        case 'renewal_cowconnect': {
          controller = endpoint.farm.contract.renewal_cowconnect.self(parameters)
          break
        }
        case 'renewal_monitoring': {
          controller = endpoint.farm.contract.renewal_monitoring.self(parameters)
          break
        }
        default: //nothing to moo
      }

      return {
        message: Translate({
          messageKey: 'do_you_really_want_to_item',
          params: {
            item: Translate({
              lower: true,
              messageKey: 'remove_item',
              params: {
                item: params?.contractName as string,
              },
            }),
          },
        }),
        save: { controller, method: 'delete' },
        subtitle: params?.contractName,
        title: Translate({
          messageKey: 'remove_item',
          params: {
            item: Translate({ lower: true, messageKey: 'contract' }),
          },
        }),
      }
    },
  },

  monitoring_activation: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.activation.form(params),
      save: {
        controller: endpoint.farm.contract.activation.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'monitoring_activation' }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.activation.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.activation.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'monitoring_activation' }),
        },
      }),
      width: 'xs',
    }),
  },

  monitoring_contract: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.monitoring.form(params),
      save: {
        controller: endpoint.farm.contract.monitoring.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'monitoring_contract' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.monitoring.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.monitoring.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'monitoring_contract' }),
        },
      }),
      width: 'md',
    }),
  },

  reactivation_cowconnect: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.reactivation_cowconnect.form(params),
      save: {
        controller: endpoint.farm.contract.reactivation_cowconnect.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'reactivation_cowconnect' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.reactivation_cowconnect.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.reactivation_cowconnect.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'reactivation_cowconnect',
          }),
        },
      }),
      width: 'md',
    }),
  },

  reactivation_monitoring: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.reactivation_monitoring.form(params),
      save: {
        controller: endpoint.farm.contract.reactivation_monitoring.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'reactivation_monitoring' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.reactivation_monitoring.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.reactivation_monitoring.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({
            lower: true,
            messageKey: 'reactivation_monitoring',
          }),
        },
      }),
      width: 'md',
    }),
  },

  reinstate_contract: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.reinstate.form(params),
      save: {
        controller: endpoint.farm.contract.reinstate.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'reinstate_contract' }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.reinstate.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.reinstate.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'reinstate_contract' }),
        },
      }),
      width: 'xs',
    }),
  },

  renewal_cowconnect: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.renewal_cowconnect.form(params),
      save: {
        controller: endpoint.farm.contract.renewal_cowconnect.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'renewal_cowconnect' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.renewal_cowconnect.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.renewal_cowconnect.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'renewal_cowconnect' }),
        },
      }),
      width: 'md',
    }),
  },

  renewal_monitoring: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.renewal_monitoring.form(params),
      save: {
        controller: endpoint.farm.contract.renewal_monitoring.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'renewal_monitoring' }),
      width: 'md',
    }),
    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.renewal_monitoring.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.renewal_monitoring.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'post',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'renewal_monitoring' }),
        },
      }),
      width: 'md',
    }),
  },

  temporary_suspension: {
    register: ({ params }: FormProps<WithFarm>): UseFormsProps => ({
      controller: endpoint.farm.contract.suspension.form(params),
      save: {
        controller: endpoint.farm.contract.suspension.register(params),
        method: 'post',
      },
      title: Translate({ messageKey: 'temporary_suspension' }),
      width: 'xs',
    }),

    update: ({
      params,
    }: FormProps<WithFarm & { contractId: string; contractName: string }>): UseFormsProps => ({
      controller: endpoint.farm.contract.suspension.self_form({
        contract: params?.contractId,
        farm: params?.farm,
      }),
      save: {
        controller: endpoint.farm.contract.suspension.self({
          contract: params?.contractId,
          farm: params?.farm,
        }),
        method: 'put',
      },
      subtitle: params?.contractName,
      title: Translate({
        messageKey: 'update_item',
        params: {
          item: Translate({ lower: true, messageKey: 'temporary_suspension' }),
        },
      }),
      width: 'xs',
    }),
  },
}

export { contractForms }
